import { CharacterMorphs } from "./CharacterMorphs";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { booksInSeries, pageTitle, SERIES, type BookEntry } from "../config/site";
import type { CharacterPage as CharacterData } from "../data/character-pages";
import { compendiumSlug } from "../data/compendium";
import { describeChapterList, type KeyItemEntry } from "../data/key-items";
import { loadPortraitUrls } from "../data/load-book-home";
import { BookSpoilers } from "../components/BookSpoilers";
import { NotFoundPage } from "./NotFoundPage";

interface BookPart {
  book: BookEntry;
  character: CharacterData;
  items: { name: string; chapterIds: string[] }[];
}

interface Loaded {
  parts: BookPart[];
  imageUrl: string | undefined;
  lookUrl: (id: string) => string | undefined;
}

async function heldItems(book: BookEntry, id: string): Promise<BookPart["items"]> {
  const response = await fetch(`/book-data/${book.id}/key-items.json`);
  if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
    return [];
  }
  const { items } = await response.json() as { items: KeyItemEntry[] };
  return items.flatMap((item) => {
    const held = item.holders.find((holder) => holder.characterId === id);
    return held === undefined ? [] : [{ name: item.name, chapterIds: held.chapterIds }];
  });
}

/** Everything about one character, a book at a time, in series order. */
async function findCharacter(books: readonly BookEntry[], id: string): Promise<Loaded | null> {
  const parts: BookPart[] = [];
  for (const book of books) {
    const response = await fetch(`/book-data/${book.id}/character-pages.json`);
    if (response.ok && (response.headers.get("content-type") ?? "").includes("json")) {
      const { characters } = await response.json() as { characters: CharacterData[] };
      const character = characters.find((item) => item.id === id);
      if (character !== undefined) {
        parts.push({ book, character, items: await heldItems(book, id) });
      }
    }
  }
  const last = parts.at(-1);
  if (last === undefined) {
    return null;
  }
  // Portraits follow the story, so this page shows the first one, which spoils nothing.
  const urlOf = await loadPortraitUrls(parts[0]!.book.id);
  return { parts, imageUrl: urlOf(id), lookUrl: urlOf };
}

export function CharacterPage() {
  const { seriesSlug, characterId } = useParams();
  const series = SERIES.find((item) => item.slug === seriesSlug);
  const [state, setState] = useState<Loaded | "loading" | "missing">("loading");

  useEffect(() => {
    if (series === undefined || characterId === undefined) {
      setState("missing");
      return;
    }
    void findCharacter(booksInSeries(series.id), characterId).then((found) => {
      setState(found ?? "missing");
      if (found !== null) {
        document.title = pageTitle(found.parts.at(-1)!.character.displayName, series.title);
      }
    });
  }, [characterId, series]);

  if (state === "missing") {
    return <NotFoundPage />;
  }
  if (state === "loading") {
    return <div className="landing" />;
  }
  const { parts, imageUrl, lookUrl } = state;
  const name = parts.at(-1)!.character.displayName;
  return (
    <div className="landing character-page">
      {imageUrl === undefined
        ? <span className="character-page__face" aria-hidden="true" />
        : <img className="character-page__face" src={imageUrl} alt="" />}
      <h1 className="landing-title">{name}</h1>
      {parts.map(({ book, character, items }) => (
        <section key={book.id} className="character-page__book" aria-label={book.title}>
          <h2 className="book-section-title">{book.title}</h2>
          <p className="character-page__from">
            In {character.scenes} {character.scenes === 1 ? "scene" : "scenes"}
          </p>
          {character.description === undefined
            ? <p className="character-page__pending">Their story so far comes when this book is finished.</p>
            : <p className="character-page__fact">{character.description}</p>}
          {character.morphedBy?.map((person) => (
            <p key={person.id} className="character-page__fact">
              <Link to={`/${seriesSlug}/characters/${person.id}`}>{person.displayName}</Link>
              {" "}can morph into {character.displayName}.
            </p>
          ))}
          <CharacterMorphs character={character} seriesSlug={seriesSlug} lookUrl={lookUrl} />
          <CharacterSpoilers
            book={book}
            character={character}
            items={items}
            seriesSlug={seriesSlug}
            lookUrl={lookUrl}
          />
        </section>
      ))}
    </div>
  );
}

/** What the story reveals about them, closed until the reader opens that book's spoilers. */
function CharacterSpoilers({ book, character, items, seriesSlug, lookUrl }: BookPart & {
  seriesSlug: string | undefined;
  lookUrl: Loaded["lookUrl"];
}) {
  const hasForm = character.morphs?.some((morph) => morph.kind === "variation") === true;
  if (!hasForm && character.partOf === undefined && character.spells === undefined
    && items.length === 0) {
    return null;
  }
  return (
    <BookSpoilers bookId={book.id} bookTitle={book.title}>
      {character.partOf?.map((person) => (
        <p key={person.id} className="character-page__fact">
          Also appears as {person.variation}, a variation of{" "}
          <Link to={`/${seriesSlug}/characters/${person.id}`}>{person.displayName}</Link>.
        </p>
      ))}
      <CharacterMorphs character={character} seriesSlug={seriesSlug} lookUrl={lookUrl} variations />
    {character.spells === undefined ? null : (
      <div className="character-page__morphs">
        <h3 className="book-section-title">Spells</h3>
        {character.spells.map((spell) => (
          <div key={spell.name} className="character-page__spell">
            <Link to={`/${seriesSlug}/spells/${compendiumSlug(spell.name)}`}><strong>{spell.name}</strong></Link>
            <p className="character-page__fact">{spell.description}</p>
            <p className="character-page__from">
              First seen in {describeChapterList([spell.chapterId])}
            </p>
          </div>
        ))}
      </div>
    )}
      {items.map((item) => (
        <p key={item.name} className="character-page__fact">
          Had the {item.name} in {describeChapterList(item.chapterIds)}.
        </p>
      ))}
    </BookSpoilers>
  );
}
